import { Css, useTestIds } from "@homebound/beam";
import { capitalCase } from "change-case";
import { useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Loading } from "src/components/Loading";
import { AiFieldProposalStatus } from "src/generated/graphqlTypes";
import { useDocumentTitle } from "src/hooks/useDocumentTitle";
import { useRouteParams } from "src/hooks/useRouteParams";
import {
  AiSourcePdfViewer,
  AiSourcePdfViewerApi,
} from "src/routes/libraries/plan-package-v2/ai-sources/AiSourcePdfViewer";
import { AiSource, AiSourceViewerHeader } from "src/routes/libraries/plan-package-v2/ai-sources/AiSourceViewerHeader";
import {
  AiSourceViewer_FieldProposalFragment,
  AiSourceViewerQuery,
  useAiSourceRowQuery,
  useAiSourceViewerQuery,
} from "src/routes/libraries/plan-package-v2/ai-sources/AiSourceViewerPage.generated";
import { PlanPackageDetailsParams } from "src/routes/routesDef";
import { queryResult } from "src/utils";

/** Full-bleed viewer for an AI import's source document, opened in a new tab from the review tabs.
 *
 * Essentially we've rebuilt Chrome's pdf viewer in order to add our own `AiSourceViewerHeader` so users can view and
 * jump between AEP/AFP sources and check the proposals against cited pages within the document.
 *
 */
export function AiSourceViewerPage() {
  const { planPackageId, planPackageVersionId } = useRouteParams<PlanPackageDetailsParams>();
  const [searchParams] = useSearchParams();
  const afpId = searchParams.get("afpId");
  const row = useAiSourceRowQuery({ variables: { afpId: afpId ?? "" }, skip: !afpId });
  const query = useAiSourceViewerQuery({ variables: { id: planPackageId, versionId: planPackageVersionId } });

  if (row.loading) return <Loading />;

  return queryResult(query, ({ planPackage }) =>
    // Deleting the document once the import is reviewed clears its `asset`
    planPackage.aiProposal?.asset ? (
      <DataView
        asset={planPackage.aiProposal.asset}
        fieldProposals={row.data?.aiFieldProposal.parent.fieldProposals ?? []}
        initialAfpId={afpId}
        initialPage={Number(searchParams.get("page")) || undefined}
      />
    ) : (
      <NoSourceDocument />
    ),
  );
}

function NoSourceDocument() {
  const tid = useTestIds({}, "noSourceDocument");
  return (
    <div css={Css.df.aic.jcc.vh100.gray700.$} {...tid}>
      This plan package version has no AI import document to show sources for.
    </div>
  );
}

type DataViewProps = {
  asset: NonNullable<NonNullable<AiSourceViewerQuery["planPackage"]["aiProposal"]>["asset"]>;
  /** Every field of the row the reviewer clicked through from. */
  fieldProposals: AiSourceViewer_FieldProposalFragment[];
  initialAfpId: string | null;
  /** From a `Page N` link. The row menu's `View Source` sends none. */
  initialPage: number | undefined;
};

function DataView(props: DataViewProps) {
  const { asset, fieldProposals, initialAfpId, initialPage } = props;
  const tid = useTestIds(props, "aiSourceViewer");
  const viewerRef = useRef<AiSourcePdfViewerApi>(null);
  const sources = useMemo(() => toAiSources(fieldProposals), [fieldProposals]);
  const [selectedId, setSelectedId] = useState(
    () => sources.find((s) => s.id === initialAfpId)?.id ?? sources.first?.id,
  );
  const selected = sources.find((s) => s.id === selectedId);
  // With no `?page=`, open on the field's first cited page rather than the cover sheet.
  const [currentPage, setCurrentPage] = useState(() => initialPage ?? selected?.pageNumbers.first ?? 1);
  // Label first, so several open sources stay distinguishable in truncated tabs.
  const setTitle = useDocumentTitle(selected ? `${selected.label} · Sources` : "Sources");

  function jumpToPage(page: number) {
    setCurrentPage(page);
    viewerRef.current?.scrollToPage(page);
  }

  function selectSource(source: AiSource) {
    setSelectedId(source.id);
    setTitle(`${source.label} · Sources`);
    const firstPage = source.pageNumbers.first;
    if (firstPage) jumpToPage(firstPage);
  }

  return (
    <div css={Css.df.fdc.vh100.oh.$} {...tid}>
      <AiSourceViewerHeader
        sources={sources}
        selected={selected}
        onSelect={selectSource}
        currentPage={currentPage}
        onSelectPage={jumpToPage}
      />
      <AiSourcePdfViewer ref={viewerRef} asset={asset} currentPage={currentPage} onPageChange={setCurrentPage} />
    </div>
  );
}

/**
 * One source per pending field that cites pages, as `Field: value`, e.g. `Stories: 2`. A room's fields all
 * repeat the room's pages, since the agent rates a room as a whole; program data's cite their own.
 */
function toAiSources(fieldProposals: AiSourceViewer_FieldProposalFragment[]): AiSource[] {
  return fieldProposals.flatMap((afp) =>
    afp.status.code === AiFieldProposalStatus.Pending && afp.pageNumbers?.length
      ? [
          {
            id: afp.id,
            // A null value is a proposal to clear the field.
            label: `${capitalCase(afp.fieldName)}: ${afp.fieldValue ?? "—"}`,
            pageNumbers: [...afp.pageNumbers].sort((a, b) => a - b),
          },
        ]
      : [],
  );
}
